import { useContext, useId } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  getFirstCollision,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  dataLocationToPath,
  evaluateTemplateValue,
  evaluateTemplateValueCollection,
} from '@ea-lab/reactive-json';
import { planBoardMove } from './boardMove';

/**
 * Tableau à colonnes avec glisser-déposer, piloté par YAML — trio générique
 * `DropBoard` / `DropColumn` / `DragCard`.
 *
 * JUSTIFICATION JSX (rituel config-first) : le rendu du tableau, ses colonnes,
 * ses cartes et leur tri restent 100 % déclaratifs (Switch + gabarits) ; ce qui
 * ne l'est pas, c'est le GESTE — capture du pointeur, cible de dépôt, pendant
 * clavier. Aucune primitive reactive-json ne l'exprime, et le projet a déjà
 * tranché pour @dnd-kit ailleurs (kanban des tâches, réordonnancement des
 * étapes de pipeline). Le plugin ne connaît donc AUCUN métier : la collection à
 * corriger, la clé d'identité, les champs à réécrire et l'URL d'enregistrement
 * sont déclarés dans le YAML.
 *
 * ── Contrat YAML ────────────────────────────────────────────────────────────
 *   - type: DropBoard
 *     collection: ~~.esResult.contents    # la liste à corriger sur place
 *     itemKey: nid                        # champ qui identifie une carte
 *     patch: { stage_tid: id, stage_name: label }   # champ de l'item ← clé de la colonne
 *     saveUrl: "/decoupled-api/save?…&entity_id="   # l'id de la carte est ajouté au bout
 *     savePayload: { stage: id }          # champ Drupal ← clé de la colonne
 *     errorPath: ~~.esError               # message en cas de refus serveur
 *     dropPath: ~~.lastDrop               # (facultatif) le dernier dépôt ENREGISTRÉ
 *     content: [ … les colonnes … ]
 *
 *   - type: DropColumn
 *     dropId: ~.value                     # identifiant de la cible (unique)
 *     columnData: { id: ~.value, label: ~.label }   # ce que le dépôt lira
 *     label: ~.label                      # pour les annonces lecteur d'écran
 *     class: "…"  overClass: "…"          # style, et style pendant le survol
 *     content: [ … ]
 *
 *   - type: DragCard
 *     dragId: ~.nid
 *     label: ~.title                      # annonces + libellé de la poignée
 *     actions: [ … ]                      # `hide` porté ICI (pas sur le contenu)
 *     content: [ … la carte … ]
 *
 * ── Ce que fait un dépôt ────────────────────────────────────────────────────
 * 1. correction OPTIMISTE de l'item dans `collection` (la carte change de
 *    colonne tout de suite, sans attendre le serveur ni un ré-index) ;
 * 2. POST des SEULS champs de `savePayload` sur `saveUrl + id` — même contrat
 *    que le kanban des tâches (`{data: {…}}`, jeton CSRF ajouté globalement par
 *    services/fetchPatch.js) ;
 * 3. en cas d'échec : retour à l'état précédent + message dans `errorPath` ;
 * 4. en cas de succès : `dropPath` reçoit `{ itemId, itemLabel, column, saveUrl }`
 *    (`saveUrl` = l'URL complète de la carte). C'est la seule prise du YAML sur
 *    le geste : un `Modal` peut s'y ouvrir pour demander un complément, et
 *    l'enregistrer sur `saveUrl` — une colonne qui l'exige le déclare dans son
 *    `columnData`. Écrit APRÈS le 2xx : un déplacement refusé ne demande rien.
 *
 * ── Accessibilité ───────────────────────────────────────────────────────────
 * Le déplacement passe par une POIGNÉE (`<button>`), pas par la carte entière :
 * la carte reste un lien activable au clavier, et la poignée porte le capteur
 * clavier de @dnd-kit (Espace pour saisir, flèches pour changer de colonne,
 * Échap pour annuler). Les annonces lecteur d'écran sont en français.
 */

/** Flèches qui font sauter la carte d'une colonne à l'autre. */
const ARROW_CODES = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

/** Colonne cible d'un dépôt, telle que déclarée par `DropColumn`. */
const columnOf = (event) => event?.over?.data?.current?.column ?? null;

/**
 * Déplacement AU CLAVIER d'une colonne à l'autre.
 *
 * Le pendant clavier par défaut de @dnd-kit avance de 25 px par flèche : il
 * faudrait une dizaine d'appuis pour traverser une colonne. Et
 * `sortableKeyboardCoordinates` (le kanban des tâches) ne convient pas ici :
 * il exige que l'élément déplacé soit LUI-MÊME une cible de dépôt, ce que les
 * cartes ne sont pas — seules les colonnes le sont. On saute donc directement
 * dans la colonne la plus proche dans la direction demandée.
 */
const boardKeyboardCoordinates = (event, { context }) => {
  const { active, collisionRect, droppableRects, droppableContainers, over } = context;
  if (!ARROW_CODES.includes(event.code)) return undefined;
  event.preventDefault();
  if (!active || !collisionRect) return undefined;

  // Les colonnes situées DEVANT la carte, dans la direction demandée.
  const ahead = [];
  droppableContainers.getEnabled().forEach((entry) => {
    if (!entry || entry.disabled) return;
    const rect = droppableRects.get(entry.id);
    if (!rect) return;
    const isAhead =
      event.code === 'ArrowRight' ? collisionRect.left < rect.left
        : event.code === 'ArrowLeft' ? collisionRect.left > rect.left
          : event.code === 'ArrowDown' ? collisionRect.top < rect.top
            : collisionRect.top > rect.top;
    if (isAhead) ahead.push(entry);
  });

  const collisions = closestCorners({ active, collisionRect, droppableRects, droppableContainers: ahead, pointerCoordinates: null });
  let closestId = getFirstCollision(collisions, 'id');
  // Ne pas rester sur la colonne courante quand il y a mieux derrière.
  if (closestId === over?.id && collisions.length > 1) closestId = collisions[1].id;
  const target = closestId != null ? droppableRects.get(closestId) : null;
  if (!target) return undefined;
  // On dépose la carte en haut de la colonne visée : c'est là qu'elle ira.
  return { x: target.left + 8, y: target.top + 8 };
};

/** Annonces lecteur d'écran (@dnd-kit les rend dans une région live). */
const announcements = {
  onDragStart: ({ active }) =>
    `Déplacement de « ${active?.data?.current?.label ?? active?.id} » commencé.`,
  onDragOver: ({ over }) =>
    (over ? `Au-dessus de la colonne « ${over?.data?.current?.label ?? over?.id} ».` : 'En dehors de toute colonne.'),
  onDragEnd: ({ active, over }) =>
    (over
      ? `« ${active?.data?.current?.label ?? active?.id} » déposé dans « ${over?.data?.current?.label ?? over?.id} ».`
      : `Déplacement de « ${active?.data?.current?.label ?? active?.id} » annulé.`),
  onDragCancel: ({ active }) =>
    `Déplacement de « ${active?.data?.current?.label ?? active?.id} » annulé.`,
};

const screenReaderInstructions = {
  draggable:
    'Pour déplacer cette carte, appuyez sur Espace ou Entrée. Utilisez les flèches pour choisir une colonne, ' +
    'Espace ou Entrée pour déposer, Échap pour annuler.',
};

export const DropBoard = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const gdc = globalDataContext.getRootContext ? globalDataContext.getRootContext() : globalDataContext;
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext: gdc, templateContext });
  const toPath = (loc) =>
    (typeof loc === 'string'
      ? dataLocationToPath({ dataLocation: loc, currentPath: templateContext?.templatePath, globalDataContext: gdc, templateContext })
      : null);

  // 3 px avant d'armer le glissement : un simple clic sur la poignée ne doit pas
  // démarrer un déplacement (même réglage que le kanban des tâches).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: boardKeyboardCoordinates }),
  );

  // Le pointeur d'abord (le curseur est DANS une colonne), coins les plus
  // proches en repli — le cas du glissement au clavier, qui n'a pas de pointeur.
  const collisionDetection = (args) => {
    const hits = pointerWithin(args);
    return hits.length > 0 ? hits : closestCorners(args);
  };

  const onDragEnd = (event) => {
    const column = columnOf(event);
    // `active.id` identifie l'INSTANCE déplacée (cf. DragCard) ; la carte porte
    // son identifiant métier dans ses données.
    const itemId = event?.active?.data?.current?.itemId;
    const plan = planBoardMove({
      items: resolve(props.collection),
      itemKey: props.itemKey || 'id',
      patch: props.patch,
      itemId,
      column,
    });
    if (!plan) return;

    const basePath = toPath(props.collection);
    const errorPath = toPath(props.errorPath);
    const dropPath = toPath(props.dropPath);
    const write = (pairs) => pairs.forEach(([field, value]) => gdc.updateData(value, `${basePath}.${plan.index}.${field}`));
    const setError = (message) => { if (errorPath) gdc.updateData(message, errorPath); };

    write(plan.writes);
    setError('');

    const saveUrl = resolve(props.saveUrl);
    const itemUrl = saveUrl ? `${saveUrl}${encodeURIComponent(itemId)}` : '';
    const itemLabel = event?.active?.data?.current?.label ?? '';
    const publishDrop = () => {
      if (dropPath) gdc.updateData({ itemId, itemLabel, column, saveUrl: itemUrl }, dropPath);
    };
    if (!saveUrl) {
      publishDrop();
      return;
    }
    const payload = {};
    for (const [field, key] of Object.entries(props.savePayload || {})) payload[field] = column[key];

    fetch(itemUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok || body?.status?.type === 'error') {
          throw new Error(body?.status?.message || `HTTP ${response.status}`);
        }
        publishDrop();
      })
      .catch((error) => {
        write(plan.previous);
        setError(`Déplacement impossible : ${error.message}`);
      });
  };

  return (
    <ActionDependant {...props}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragEnd={onDragEnd}
        accessibility={{ announcements, screenReaderInstructions }}
      >
        <View props={props.content} path={`${path}.content`} datafield="content" currentData={currentData?.content} />
      </DndContext>
    </ActionDependant>
  );
};

export const DropColumn = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext });
  const label = resolve(props.label) ?? '';
  const column = evaluateTemplateValueCollection({
    valueToEvaluate: props.columnData ?? {},
    globalDataContext,
    templateContext,
    evaluationDepth: 2,
  });
  const { setNodeRef, isOver } = useDroppable({
    id: String(resolve(props.dropId) ?? ''),
    data: { column, label },
  });

  return (
    <ActionDependant {...props}>
      <div ref={setNodeRef} className={`${props.class ?? ''} ${isOver ? (props.overClass ?? '') : ''}`}>
        <View props={props.content} path={`${path}.content`} datafield="content" currentData={currentData?.content} />
      </div>
    </ActionDependant>
  );
};

export const DragCard = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext });
  const id = resolve(props.dragId);
  const label = String(resolve(props.label) ?? '');
  // PIÈGE : un gabarit de carte est instancié dans CHAQUE colonne — `hide` ne
  // supprime que le rendu, pas l'exécution du composant. Cinq colonnes = cinq
  // `useDraggable` pour la même transaction : avec l'identifiant métier comme
  // identité de glissement, la dernière instance (invisible, donc sans nœud
  // DOM) écrasait les autres dans le registre @dnd-kit, et le glissement
  // n'aboutissait jamais. L'identité de GLISSEMENT est donc propre à
  // l'instance (`useId`) ; l'identifiant MÉTIER voyage dans `data.itemId`.
  const dragKey = useId();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragKey,
    data: { itemId: id, label },
  });

  const body = (
    <View props={props.content} path={`${path}.content`} datafield="content" currentData={currentData?.content} />
  );

  // Sans identifiant, la carte reste lisible mais n'est pas déplaçable — mieux
  // qu'une poignée qui ne mènerait nulle part.
  if (id === null || id === undefined || id === '') {
    return <ActionDependant {...props}>{body}</ActionDependant>;
  }

  return (
    <ActionDependant {...props}>
      <div
        ref={setNodeRef}
        className={`relative ${props.class ?? ''}`}
        style={{
          transform: CSS.Translate.toString(transform),
          opacity: isDragging ? 0.4 : undefined,
          zIndex: isDragging ? 20 : undefined,
        }}
      >
        {body}
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Déplacer ${label}`}
          title="Déplacer vers une autre colonne"
          className="absolute top-1 right-1 px-1 leading-none text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-400"
        >
          ⠿
        </button>
      </div>
    </ActionDependant>
  );
};
